import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Tabs } from '@querodelivery/uni-react';
import { useSearchParams } from 'react-router-dom';
import JsonView from '@/Shared/JsonView';
import { PreRegistrationList } from './components/pre-registration-list';
import { FormSearch } from './components/form-search';
import { useGetPreRegistrations } from './api/get-pre-registrations';
import type {
  PreRegistrationListFormType,
  PreRegistrationsParams,
} from './types';
import { LoadingPage } from '@/_components/loading-page';
import { useCities } from '@/_hooks/use-cities';
import { PartnersList } from './components/partners-list/partners-list';
import {
  getPreRegistrationTabFromSearchParams,
  PRE_REGISTRATION_TAB_IDS,
  PRE_REGISTRATION_TAB_ITEMS,
  PRE_REGISTRATION_TAB_SEARCH_PARAM,
  PRE_REGISTRATION_TABS,
  type PreRegistrationTabId,
} from './constants';

function PreRegistrationMain() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getPreRegistrationTabFromSearchParams(searchParams);

  const [params, setParams] = useState<PreRegistrationsParams>({
    cityId: '',
    deliverymanName: '',
    preRegistrationFilters: [],
    sendPreRegistrationAt: undefined,
    vehicleType: '',
  });
  const [page, setPage] = useState(1);

  const isDeliverymenTab = activeTab === PRE_REGISTRATION_TABS.DELIVERYMEN;

  const { data, isFetching: isLoadingDeliverymen } = useGetPreRegistrations(
    {
      ...params,
      page,
    },
    { enabled: isDeliverymenTab },
  );

  const { data: deliverymen, totalCount } = data || {};

  const { loadingCities } = useCities();

  const handleSubmitSearch = (data: PreRegistrationListFormType) => {
    setParams(data);
  };

  useEffect(() => {
    const tab = searchParams.get(PRE_REGISTRATION_TAB_SEARCH_PARAM);
    const isValidTab =
      tab && PRE_REGISTRATION_TAB_IDS.includes(tab as PreRegistrationTabId);

    if (!isValidTab) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(
            PRE_REGISTRATION_TAB_SEARCH_PARAM,
            PRE_REGISTRATION_TABS.DELIVERYMEN,
          );
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (tabId === activeTab) {
        return;
      }

      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set(PRE_REGISTRATION_TAB_SEARCH_PARAM, tabId);
        return next;
      });
    },
    [activeTab],
  );

  const tabMenus: Record<PreRegistrationTabId, ReactElement> = {
    [PRE_REGISTRATION_TABS.DELIVERYMEN]: loadingCities ? (
      <LoadingPage isLoading />
    ) : (
      <>
        <FormSearch params={params} onRequest={handleSubmitSearch} />

        <PreRegistrationList
          isLoadingDeliverymen={isLoadingDeliverymen}
          setPage={setPage}
          page={page}
          deliverymen={deliverymen || []}
          totalCount={totalCount || 0}
        />

        <JsonView name="Pre-cadastro" source={deliverymen} />
      </>
    ),
    [PRE_REGISTRATION_TABS.PARTNERS]: <PartnersList />,
  };

  return (
    <div>
      <Tabs
        activeId={activeTab}
        layout="inline"
        size="lg"
        tabs={PRE_REGISTRATION_TAB_ITEMS}
        onChange={handleTabChange}
      >
        {(activeId: string) => tabMenus[activeId as PreRegistrationTabId]}
      </Tabs>
    </div>
  );
}

export { PreRegistrationMain };
